import type { ApprovalDecision } from "@/lib/decide-approval";
import { getAppName } from "@/components/shapes/connection-frame/app-icons";
import { translate } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import { workToastPrimaryOpenTarget } from "@/lib/work-toast-apps";
import type {
  WorkToastItem,
  WorkToastOpenTarget,
} from "@/stores/work-toast-store";
import type { ToastMenuItem } from "./toast-more-menu";
import type { ToastPillAction } from "./toast-pill";
import {
  openWorkToastAppTarget,
  requestWorkToastTask,
  stopWorkToast,
} from "./work-toast-actions";
import type {
  WorkToastClick,
  WorkToastHoverId,
  WorkToastMenuId,
} from "./work-toast-view";

/** Only explicit destination links can be opened independently. */
export function workToastTargetOfferable(target: WorkToastOpenTarget): boolean {
  return Boolean(target.uri ?? target.url);
}

/** Dispatches a pill's body click. Only "open-task" is pure —
 *  "open-chat" and "expand" depend on component-local state, so the pill
 *  supplies those through `deps`. */
export function dispatchWorkToastClick(
  click: WorkToastClick,
  item: WorkToastItem,
  deps: {
    openChat: () => void;
    expand: () => void;
    replyCapable: boolean;
    setOpenedVia: (via: "reply") => void;
  },
): void {
  switch (click) {
    case "none":
      return;
    case "open-chat":
      deps.openChat();
      return;
    case "open-task":
      requestWorkToastTask(item);
      return;
    case "expand":
      // Only a reply-capable state (question / done-with-follow-up) should
      // claim initial card focus for the reply textarea.
      if (deps.replyCapable) deps.setOpenedVia("reply");
      deps.expand();
      return;
  }
}

export interface WorkToastHoverDeps {
  /** Toggle-and-register a voice reply; undefined when dictation is unavailable. */
  micAction?: { disabled?: boolean; onClick: () => void };
  /** A question is answered; anything else is followed up. */
  reply: "answer" | "followUp";
  expand: () => void;
  setOpenedVia: (via: "reply") => void;
  busy: boolean;
  decide: (decision: ApprovalDecision) => void;
}

/** Builds the pill's hover actions (spec §4.2/§4.4). Recomputed each render
 *  since `deps` carries render-local state (dictation, busy). */
export function buildWorkToastHoverActions(
  item: WorkToastItem,
  deps: WorkToastHoverDeps,
): Record<WorkToastHoverId, ToastPillAction> {
  return {
    stop: {
      id: "stop",
      label: translate(toastsMessages, "stop"),
      onClick: () => void stopWorkToast(item),
    },
    mic: {
      id: "mic",
      label: translate(
        toastsMessages,
        deps.reply === "answer" ? "answerByVoice" : "followUpByVoice",
      ),
      icon: "Mic",
      disabled: deps.micAction?.disabled,
      onClick: () => {
        // Claim, register and toggle first, then expand (spec §4.4).
        deps.micAction?.onClick();
        deps.setOpenedVia("reply");
        deps.expand();
      },
    },
    type: {
      id: "type",
      label: translate(
        toastsMessages,
        deps.reply === "answer" ? "typeAnswer" : "typeFollowUp",
      ),
      icon: "Type",
      onClick: () => {
        deps.setOpenedVia("reply");
        deps.expand();
      },
    },
    deny: {
      id: "deny",
      label: translate(toastsMessages, "deny"),
      icon: "X",
      disabled: deps.busy || !item.projectId,
      onClick: () => void deps.decide("reject"),
    },
    allow: {
      id: "allow",
      label: translate(toastsMessages, "allow"),
      icon: "Check",
      disabled: deps.busy || !item.projectId,
      onClick: () => void deps.decide("approve"),
    },
  };
}

export interface WorkToastMenuDeps {
  openChat: () => void;
  primaryOpenTarget: WorkToastOpenTarget | null;
  dismiss: () => void;
}

function openInAppLabel(item: WorkToastItem): string {
  const primary = workToastPrimaryOpenTarget(item);
  return primary
    ? translate(toastsMessages, "openInApp", { app: getAppName(primary.appId) })
    : translate(toastsMessages, "openInAppFallback");
}

/** Builds the pill's ⋮ menu actions (spec §4.2). */
export function buildWorkToastMenuActions(
  item: WorkToastItem,
  deps: WorkToastMenuDeps,
): Record<WorkToastMenuId, ToastMenuItem> {
  return {
    "open-chat": {
      id: "open-chat",
      label: translate(toastsMessages, "openChat"),
      onSelect: deps.openChat,
    },
    "open-task": {
      id: "open-task",
      label: translate(toastsMessages, "openTask"),
      onSelect: () => requestWorkToastTask(item),
    },
    "open-in-app": {
      id: "open-in-app",
      label: openInAppLabel(item),
      onSelect: () => {
        if (deps.primaryOpenTarget)
          openWorkToastAppTarget(deps.primaryOpenTarget);
      },
    },
    dismiss: {
      id: "dismiss",
      label: translate(toastsMessages, "clear"),
      onSelect: deps.dismiss,
    },
  };
}
